import React from 'react';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { TrendingUpIcon, UsersIcon, ShieldIcon, AwardIcon, CheckCircle2Icon } from 'lucide-react';
export function CorporateWellness() {
  const benefits = [{
    icon: TrendingUpIcon,
    title: 'Improved Productivity',
    desc: 'Metabolically healthy employees have sustained energy, sharper focus, and less brain fog.'
  }, {
    icon: ShieldIcon,
    title: 'Reduced Healthcare Costs',
    desc: 'Addressing root causes prevents chronic diseases that drive up corporate insurance premiums.'
  }, {
    icon: UsersIcon,
    title: 'Higher Engagement',
    desc: 'Teams that train together and prioritize health build stronger bonds and morale.'
  }, {
    icon: AwardIcon,
    title: 'Stronger Leadership',
    desc: 'Physical discipline translates directly to mental resilience and leadership capacity.'
  }];
  const inclusions = ['Metabolic health education & seminars', 'Executive blood work analysis', 'Company-wide strength training programs', 'Lifestyle & sleep optimization protocols', 'Mindset & resilience development', 'Interactive workshops and Q&A sessions'];
  return <main className="w-full pt-24" data-id="element-269">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-dark relative border-b border-border overflow-hidden" data-id="element-270">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-271">
          <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070" alt="Corporate Team" className="w-full h-full object-cover" data-id="element-272" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-transparent" data-id="element-273"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-274">
          <AnimatedSection data-id="element-275">
            <span className="text-gold text-sm font-medium uppercase tracking-[0.3em] mb-6 block" data-id="element-276">
              Elevate Your Organization's Performance
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-heading leading-tight" data-id="element-277">
              Corporate Wellness
            </h1>
            <p className="text-xl text-muted leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-278">
              Transform your company culture by investing in the metabolic
              health, physical strength, and mental resilience of your team.
            </p>
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-279">
              Schedule a Consultation
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-280">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-281">
          <AnimatedSection data-id="element-282">
            <SectionHeading title="The ROI of Health" overline="Benefits" data-id="element-283" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16" data-id="element-284">
            {benefits.map((item, i) => <AnimatedSection key={i} delay={i * 0.1} data-id="element-285">
                <div className="bg-dark border border-border p-10 h-full flex items-start" data-id="element-286">
                  <item.icon className="w-12 h-12 text-gold mr-6 flex-shrink-0" data-id="element-287" />
                  <div data-id="element-288">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-289">
                      {item.title}
                    </h3>
                    <p className="text-muted leading-relaxed" data-id="element-290">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-291">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-292">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-293">
            <AnimatedSection direction="right" data-id="element-294">
              <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=2070" alt="Presentation" className="w-full aspect-video object-cover border border-border" data-id="element-295" />
            </AnimatedSection>

            <AnimatedSection direction="left" data-id="element-296">
              <SectionHeading overline="The Curriculum" title="What's Included" align="left" description="Customizable programs designed to fit your organization's size, goals, and culture." data-id="element-297" />
              <ul className="mt-10 space-y-5" data-id="element-298">
                {inclusions.map((item, i) => <li key={i} className="flex items-center" data-id="element-299">
                    <CheckCircle2Icon className="w-5 h-5 text-gold mr-4 flex-shrink-0" data-id="element-300" />
                    <span className="text-white text-lg" data-id="element-301">{item}</span>
                  </li>)}
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-surface border-t border-border text-center" data-id="element-302">
        <AnimatedSection data-id="element-303">
          <h2 className="text-4xl font-bold text-white mb-8 font-heading" data-id="element-304">
            Build a Resilient Team
          </h2>
          <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-305">
            Contact Our Corporate Team
          </Button>
        </AnimatedSection>
      </section>
    </main>;
}